import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

type Tone = "primary" | "danger" | "warning";

type ConfirmOptions = {
  title?: string;
  message: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: Tone;
};

type AlertOptions = {
  title?: string;
  message: ReactNode;
  okLabel?: string;
  tone?: Tone;
};

type PromptOptions = {
  title?: string;
  message: ReactNode;
  defaultValue?: string;
  placeholder?: string;
  okLabel?: string;
  cancelLabel?: string;
  type?: "text" | "date" | "email" | "number";
  // Returning a non-empty string blocks submission and shows the message under the input.
  validate?: (value: string) => string | null;
  tone?: Tone;
};

type DialogContextValue = {
  confirm: (opts: ConfirmOptions) => Promise<boolean>;
  alert: (opts: AlertOptions) => Promise<void>;
  prompt: (opts: PromptOptions) => Promise<string | null>;
};

const DialogContext = createContext<DialogContextValue | null>(null);

type ActiveDialog =
  | { kind: "confirm"; opts: ConfirmOptions; resolve: (v: boolean) => void }
  | { kind: "alert"; opts: AlertOptions; resolve: () => void }
  | { kind: "prompt"; opts: PromptOptions; resolve: (v: string | null) => void };

export function DialogProvider({ children }: { children: ReactNode }) {
  const [active, setActive] = useState<ActiveDialog | null>(null);

  const close = useCallback(() => setActive(null), []);

  const confirm = useCallback(
    (opts: ConfirmOptions) =>
      new Promise<boolean>((resolve) => {
        setActive({
          kind: "confirm",
          opts,
          resolve: (v) => {
            resolve(v);
            setActive(null);
          },
        });
      }),
    [],
  );

  const alertFn = useCallback(
    (opts: AlertOptions) =>
      new Promise<void>((resolve) => {
        setActive({
          kind: "alert",
          opts,
          resolve: () => {
            resolve();
            setActive(null);
          },
        });
      }),
    [],
  );

  const promptFn = useCallback(
    (opts: PromptOptions) =>
      new Promise<string | null>((resolve) => {
        setActive({
          kind: "prompt",
          opts,
          resolve: (v) => {
            resolve(v);
            setActive(null);
          },
        });
      }),
    [],
  );

  const value = useMemo(
    () => ({ confirm, alert: alertFn, prompt: promptFn }),
    [confirm, alertFn, promptFn],
  );

  return (
    <DialogContext.Provider value={value}>
      {children}
      {active && <DialogHost active={active} onClose={close} />}
    </DialogContext.Provider>
  );
}

function DialogHost({
  active,
  onClose,
}: {
  active: ActiveDialog;
  onClose: () => void;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        // Esc cancels: returns false / "" / cancellation, depending on dialog type
        if (active.kind === "confirm") active.resolve(false);
        else if (active.kind === "prompt") active.resolve(null);
        else active.resolve();
        onClose();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [active, onClose]);

  return (
    <div
      onClick={() => {
        // Backdrop click = cancel (same as Esc)
        if (active.kind === "confirm") active.resolve(false);
        else if (active.kind === "prompt") active.resolve(null);
        else active.resolve();
      }}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15, 23, 42, 0.55)",
        // Above drawers (FileExplorer is 100, side modals are <= 150).
        zIndex: 200,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "1rem",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        style={{
          width: "min(480px, 100%)",
          background: "white",
          borderRadius: "0.65rem",
          boxShadow: "0 20px 50px rgba(16, 30, 54, .25)",
          overflow: "hidden",
        }}
      >
        {active.kind === "confirm" && (
          <ConfirmBody
            opts={active.opts}
            onCancel={() => active.resolve(false)}
            onConfirm={() => active.resolve(true)}
          />
        )}
        {active.kind === "alert" && (
          <AlertBody opts={active.opts} onOk={() => active.resolve()} />
        )}
        {active.kind === "prompt" && (
          <PromptBody
            opts={active.opts}
            onCancel={() => active.resolve(null)}
            onSubmit={(v) => active.resolve(v)}
          />
        )}
      </div>
    </div>
  );
}

function ConfirmBody({
  opts,
  onCancel,
  onConfirm,
}: {
  opts: ConfirmOptions;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const confirmRef = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    confirmRef.current?.focus();
  }, []);
  return (
    <>
      <DialogHeader title={opts.title ?? "Confirmar acción"} tone={opts.tone} />
      <div style={bodyStyle}>{opts.message}</div>
      <Footer>
        <button type="button" onClick={onCancel} style={cancelBtn}>
          {opts.cancelLabel ?? "Cancelar"}
        </button>
        <button
          type="button"
          onClick={onConfirm}
          style={primaryBtn(opts.tone)}
          ref={confirmRef}
        >
          {opts.confirmLabel ?? "Confirmar"}
        </button>
      </Footer>
    </>
  );
}

function AlertBody({
  opts,
  onOk,
}: {
  opts: AlertOptions;
  onOk: () => void;
}) {
  const okRef = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    okRef.current?.focus();
  }, []);
  return (
    <>
      <DialogHeader title={opts.title ?? "Aviso"} tone={opts.tone} />
      <div style={bodyStyle}>{opts.message}</div>
      <Footer>
        <button type="button" onClick={onOk} style={primaryBtn(opts.tone)} ref={okRef}>
          {opts.okLabel ?? "Entendido"}
        </button>
      </Footer>
    </>
  );
}

function PromptBody({
  opts,
  onCancel,
  onSubmit,
}: {
  opts: PromptOptions;
  onCancel: () => void;
  onSubmit: (value: string) => void;
}) {
  const [value, setValue] = useState(opts.defaultValue ?? "");
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  const submit = () => {
    const trimmed = value.trim();
    const err = opts.validate ? opts.validate(trimmed) : null;
    if (err) {
      setError(err);
      return;
    }
    onSubmit(trimmed);
  };

  return (
    <>
      <DialogHeader title={opts.title ?? "Introduce un valor"} tone={opts.tone} />
      <form
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <div style={bodyStyle}>
          <div style={{ marginBottom: "0.55rem" }}>{opts.message}</div>
          <input
            ref={inputRef}
            type={opts.type ?? "text"}
            value={value}
            placeholder={opts.placeholder}
            onChange={(e) => {
              setValue(e.target.value);
              if (error) setError(null);
            }}
            style={inputStyle}
          />
          {error && (
            <div
              style={{
                marginTop: "0.4rem",
                fontSize: ".75rem",
                color: "#b32424",
              }}
            >
              {error}
            </div>
          )}
        </div>
        <Footer>
          <button type="button" onClick={onCancel} style={cancelBtn}>
            {opts.cancelLabel ?? "Cancelar"}
          </button>
          <button type="submit" style={primaryBtn(opts.tone)}>
            {opts.okLabel ?? "Aceptar"}
          </button>
        </Footer>
      </form>
    </>
  );
}

function DialogHeader({
  title,
  tone,
}: {
  title: string;
  tone?: Tone;
}) {
  const accent =
    tone === "danger" ? "#b32424" : tone === "warning" ? "#856404" : "#0069d9";
  return (
    <header
      style={{
        padding: "0.95rem 1.25rem 0.5rem",
        borderBottom: "1px solid #eef1f6",
        background: "white",
        borderLeft: `3px solid ${accent}`,
      }}
    >
      <h2
        style={{
          margin: 0,
          fontSize: "1rem",
          fontWeight: 600,
          color: "#2A3F54",
        }}
      >
        {title}
      </h2>
    </header>
  );
}

function Footer({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        display: "flex",
        gap: "0.5rem",
        justifyContent: "flex-end",
        padding: "0.95rem 1.25rem",
        borderTop: "1px solid #eef1f6",
        background: "#f8f9fb",
      }}
    >
      {children}
    </div>
  );
}

const bodyStyle: React.CSSProperties = {
  padding: "1rem 1.25rem",
  fontSize: ".88rem",
  color: "#1f2933",
  lineHeight: 1.5,
  whiteSpace: "pre-wrap",
};

const cancelBtn: React.CSSProperties = {
  padding: "0.5rem 1rem",
  borderRadius: "0.5rem",
  background: "white",
  color: "#73879C",
  border: "1px solid #dee2e6",
  fontSize: ".82rem",
  fontWeight: 600,
  cursor: "pointer",
};

const primaryBtn = (tone: Tone | undefined): React.CSSProperties => {
  const bg =
    tone === "danger" ? "#b32424" : tone === "warning" ? "#856404" : "#0069d9";
  return {
    padding: "0.5rem 1.1rem",
    borderRadius: "0.5rem",
    background: bg,
    color: "white",
    border: `1px solid ${bg}`,
    fontSize: ".82rem",
    fontWeight: 600,
    cursor: "pointer",
    boxShadow: "0 2px 4px rgba(0,0,0,.1)",
  };
};

const inputStyle: React.CSSProperties = {
  width: "100%",
  padding: "0.5rem 0.7rem",
  borderRadius: "0.45rem",
  border: "1px solid #dee2e6",
  background: "#f8f9fb",
  fontSize: ".9rem",
  fontFamily: "inherit",
};

export function useDialog(): DialogContextValue {
  const ctx = useContext(DialogContext);
  if (!ctx) throw new Error("useDialog must be used inside DialogProvider");
  return ctx;
}

export function useConfirm() {
  return useDialog().confirm;
}

export function useAlert() {
  return useDialog().alert;
}

export function usePrompt() {
  return useDialog().prompt;
}
